import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Toast from '../../components/Toast';
import CustomSelect from '../../components/common/CustomSelect';
import SectorListsManager from '../../components/admin/SectorListsManager';
import { settingsApi, integrationsApi, oesApi, agentApi } from '../../services/api';
import { useInstanceMode } from '../../providers/InstanceModeProvider';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

const TAB_GENERAL = 'general';
const TAB_EMAIL = 'email';
const TAB_AI = 'ai';
const TAB_INTEGRATIONS = 'integrations';
const TAB_AGENT = 'agent';

const AI_PROVIDERS = [
  { value: 'ollama', label: 'Ollama (self-hosted)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
];

const DEFAULT_MODELS = {
  ollama: ['llama3.1:latest', 'llama3.2:latest', 'mistral:latest', 'gemma2:latest'],
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: ['claude-sonnet-4-20250514', 'claude-haiku-4-20250414', 'claude-opus-4-20250514'],
};

// Fallback when scales not yet loaded
const DEFAULT_CRITICALITY_OPTIONS = [
  { value: 'MB', label: 'Peripheral' },
  { value: 'B', label: 'Supportive' },
  { value: 'M', label: 'Important' },
  { value: 'A', label: 'Essential' },
  { value: 'MA', label: 'Vital/Strategic' },
];

const SCALE_LABELS = {
  criticality: 'Criticality levels',
  impact_levels: 'Impact levels',
  user_count_levels: 'User count / reach',
  frequency_levels: 'Frequency of use',
  replaceability_levels: 'Replaceability',
  interdependency_levels: 'Interdependency',
  time_to_impact_levels: 'Time to impact',
  automation_levels: 'Automation levels',
  cyberattack_impact_levels: 'Cyberattack impact',
};

export default function Settings() {
  const labels = useLabels();
  const t = useT();
  const [scopes, setScopes] = useState({ can_edit_global: false, organizations: [] });
  const [scopesLoaded, setScopesLoaded] = useState(false);
  const [scope, setScope] = useState('global');
  const [organizationId, setOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ciiThreshold, setCiiThreshold] = useState('M');
  const [scales, setScales] = useState(null);
  const [riskMethod, setRiskMethod] = useState('max');
  const [riskResidualMode, setRiskResidualMode] = useState(false);
  const [expandedScales, setExpandedScales] = useState({});
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [activeTab, setActiveTab] = useState(TAB_GENERAL);
  const [systemSettings, setSystemSettings] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemSaving, setSystemSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_sender: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_starttls: true,
  });
  // Integration state
  const [intLoading, setIntLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [intFormOpen, setIntFormOpen] = useState(false);
  const [intEditing, setIntEditing] = useState(null);
  const [intTesting, setIntTesting] = useState(null);
  const [intTestResult, setIntTestResult] = useState(null);
  const [intSaving, setIntSaving] = useState(false);
  const [oesList, setOesList] = useState([]);
  const [intForm, setIntForm] = useState({
    name: '', integration_type: 'crowdstrike_falcon', client_id: '', client_secret: '', base_url: 'https://api.crowdstrike.com', region: 'us-1', oes_ids: [],
  });

  // Agent state (OES side — API key management)
  const { isOES: isOESMode, isCoordinator: isCoordinatorMode, pureOES } = useInstanceMode();
  const [agentKeys, setAgentKeys] = useState([]);
  const [agentKeysLoading, setAgentKeysLoading] = useState(false);
  const [agentKeyFormOpen, setAgentKeyFormOpen] = useState(false);
  const [agentKeySaving, setAgentKeySaving] = useState(false);
  const [agentKeyCreated, setAgentKeyCreated] = useState(null); // raw key shown once
  const [agentKeyForm, setAgentKeyForm] = useState({
    label: '', sharing_policy: { criticality: true, posture: true, vulnerability_summary: true, risk: true },
  });

  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiAdvancedOpen, setAiAdvancedOpen] = useState(false);
  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaModelsLoading, setOllamaModelsLoading] = useState(false);
  const [aiForm, setAiForm] = useState({
    provider: 'ollama',
    model: 'llama3.1:latest',
    host: '',
    api_key: '',
    temperature: 0.2,
    max_tokens: 4000,
  });

  // Sector dependencies state
  const [sectorDepsPreset, setSectorDepsPreset] = useState('enisa_nis2');
  const [sectorDepsMatrix, setSectorDepsMatrix] = useState({});
  const [sectorDepsLoading, setSectorDepsLoading] = useState(false);
  const [sectorDepsSaving, setSectorDepsSaving] = useState(false);

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  const loadEditableScopes = async () => {
    try {
      const res = await settingsApi.getEditableScopes();
      const data = res.data || {};
      setScopes({ can_edit_global: data.can_edit_global ?? false, organizations: data.organizations || [] });
      if (data.can_edit_global) {
        setScope('global');
        setOrganizationId(null);
      } else if (data.organizations?.length > 0) {
        setScope('organization');
        setOrganizationId(data.organizations[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load editable scopes');
    } finally {
      setScopesLoaded(true);
    }
  };

  const loadSettings = async () => {
    if (scope === 'organization' && !organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = scope === 'global'
        ? await settingsApi.get('global')
        : await settingsApi.get('organization', organizationId);
      const data = res.data || {};
      setCiiThreshold(data.cii_criticality_threshold || 'M');
      setScales(data.scales || {});
      setRiskMethod((data.risk_overall_method || 'max').toLowerCase());
      setRiskResidualMode(!!data.risk_residual_mode);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEditableScopes();
  }, []);

  useEffect(() => {
    if (!scopesLoaded) setLoading(true);
  }, [scopesLoaded]);

  useEffect(() => {
    if (!scopesLoaded) return;
    loadSettings();
  }, [scopesLoaded, scope, organizationId]);

  const loadSystemSettings = async () => {
    if (!scopes.can_edit_global) return;
    setSystemLoading(true);
    try {
      const res = await settingsApi.getSystem();
      const d = res.data || {};
      setSystemSettings(d);
      setEmailForm({
        smtp_server: d.smtp_server ?? '',
        smtp_port: d.smtp_port ?? 587,
        smtp_username: d.smtp_username ?? '',
        smtp_password: '', // never pre-fill password
        smtp_sender: d.smtp_sender ?? '',
        smtp_use_tls: d.smtp_use_tls ?? true,
        smtp_use_ssl: d.smtp_use_ssl ?? false,
        smtp_starttls: d.smtp_starttls ?? true,
      });
    } catch (e) {
      if (e.response?.status === 403) return;
      showToast('error', e.response?.data?.detail || 'Failed to load email settings');
    } finally {
      setSystemLoading(false);
    }
  };

  useEffect(() => {
    if (scopesLoaded && scopes.can_edit_global && activeTab === TAB_EMAIL) {
      loadSystemSettings();
    }
  }, [scopesLoaded, scopes.can_edit_global, activeTab]);

  const handleSaveEmail = async () => {
    setSystemSaving(true);
    setError(null);
    try {
      const payload = {
        smtp_server: emailForm.smtp_server || null,
        smtp_port: emailForm.smtp_port || null,
        smtp_username: emailForm.smtp_username || null,
        smtp_sender: emailForm.smtp_sender || null,
        smtp_use_tls: emailForm.smtp_use_tls,
        smtp_use_ssl: emailForm.smtp_use_ssl,
        smtp_starttls: emailForm.smtp_starttls,
      };
      if (emailForm.smtp_password) payload.smtp_password = emailForm.smtp_password;
      await settingsApi.putSystem(payload);
      await loadSystemSettings();
      showToast('success', 'Email settings saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSystemSaving(false);
    }
  };

  const loadAIConfig = async () => {
    if (!scopes.can_edit_global) return;
    setAiLoading(true);
    try {
      const res = await settingsApi.getAI();
      const d = res.data || {};
      setAiForm({
        provider: d.provider || 'ollama',
        model: d.model || '',
        host: d.host || '',
        api_key: '',
        temperature: d.temperature ?? 0.2,
        max_tokens: d.max_tokens ?? 4000,
      });
    } catch (e) {
      if (e.response?.status === 403) return;
      showToast('error', e.response?.data?.detail || 'Failed to load AI settings');
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (scopesLoaded && scopes.can_edit_global && activeTab === TAB_AI) {
      loadAIConfig();
    }
  }, [scopesLoaded, scopes.can_edit_global, activeTab]);

  // Integrations lazy load
  const loadIntegrations = async () => {
    setIntLoading(true);
    try {
      const [intRes, oesRes] = await Promise.all([
        integrationsApi.getAll(),
        oesApi.getAll(),
      ]);
      setIntegrations(intRes.data || []);
      setOesList(oesRes.data || []);
    } catch { /* ignore */ } finally {
      setIntLoading(false);
    }
  };
  useEffect(() => {
    if (scopesLoaded && scopes.can_edit_global && activeTab === TAB_INTEGRATIONS) {
      loadIntegrations();
    }
  }, [scopesLoaded, scopes.can_edit_global, activeTab]);

  const resetIntForm = () => {
    const defaultOesIds = pureOES && oesList.length === 1 ? [oesList[0].id] : [];
    setIntForm({ name: '', integration_type: 'crowdstrike_falcon', client_id: '', client_secret: '', base_url: 'https://api.crowdstrike.com', region: 'us-1', oes_ids: defaultOesIds });
    setIntEditing(null);
    setIntTestResult(null);
  };

  const handleIntSave = async () => {
    setIntSaving(true);
    try {
      const payload = {
        integration_type: intForm.integration_type,
        name: intForm.name,
        config: { client_id: intForm.client_id, client_secret: intForm.client_secret, base_url: intForm.base_url, region: intForm.region },
        oes_ids: intForm.oes_ids,
      };
      if (intEditing) {
        await integrationsApi.update(intEditing.id, { name: payload.name, config: payload.config, is_enabled: intEditing.is_enabled });
        // Update OES mappings
        const existingOesIds = (intEditing.oes_mappings || []).map(m => m.oes_id);
        for (const oesId of intForm.oes_ids) {
          if (!existingOesIds.includes(oesId)) await integrationsApi.addOESMapping(intEditing.id, { oes_id: oesId });
        }
        for (const oesId of existingOesIds) {
          if (!intForm.oes_ids.includes(oesId)) await integrationsApi.removeOESMapping(intEditing.id, oesId);
        }
      } else {
        await integrationsApi.create(payload);
      }
      showToast('success', intEditing ? 'Integration updated' : 'Integration created');
      setIntFormOpen(false);
      resetIntForm();
      loadIntegrations();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to save integration');
    } finally {
      setIntSaving(false);
    }
  };

  const handleIntTest = async (integrationId) => {
    setIntTesting(integrationId);
    setIntTestResult(null);
    try {
      const res = await integrationsApi.testConnection(integrationId);
      setIntTestResult({ id: integrationId, ...res.data });
    } catch (err) {
      setIntTestResult({ id: integrationId, ok: false, error: err.response?.data?.detail || 'Connection test failed' });
    } finally {
      setIntTesting(null);
    }
  };

  const handleIntDelete = async (integrationId) => {
    if (!window.confirm('Delete this integration? All sync history and findings will be removed.')) return;
    try {
      await integrationsApi.delete(integrationId);
      showToast('success', 'Integration deleted');
      loadIntegrations();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleIntToggle = async (integration) => {
    try {
      await integrationsApi.update(integration.id, { is_enabled: !integration.is_enabled });
      loadIntegrations();
    } catch { /* ignore */ }
  };

  const openIntEdit = (integration) => {
    setIntEditing(integration);
    setIntForm({
      name: integration.name,
      integration_type: integration.integration_type,
      client_id: integration.config?.client_id || '',
      client_secret: '',
      base_url: integration.config?.base_url || 'https://api.crowdstrike.com',
      region: integration.config?.region || 'us-1',
      oes_ids: (integration.oes_mappings || []).map(m => m.oes_id),
    });
    setIntFormOpen(true);
    setIntTestResult(null);
  };

  // Agent tab lazy load + handlers
  useEffect(() => {
    // Instance mode now provided by InstanceModeProvider context
  }, []);

  const loadAgentKeys = async () => {
    setAgentKeysLoading(true);
    try {
      const res = await agentApi.getKeys();
      setAgentKeys(res.data || []);
    } catch { /* ignore */ } finally {
      setAgentKeysLoading(false);
    }
  };
  useEffect(() => {
    if (scopesLoaded && scopes.can_edit_global && activeTab === TAB_AGENT) loadAgentKeys();
  }, [scopesLoaded, scopes.can_edit_global, activeTab]);

  const handleCreateAgentKey = async () => {
    setAgentKeySaving(true);
    try {
      const res = await agentApi.createKey(agentKeyForm);
      setAgentKeyCreated(res.data);
      setAgentKeyFormOpen(false);
      setAgentKeyForm({ label: '', sharing_policy: { criticality: true, posture: true, vulnerability_summary: true, risk: true } });
      loadAgentKeys();
      showToast('success', 'API key generated');
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to create key');
    } finally {
      setAgentKeySaving(false);
    }
  };

  const handleRevokeAgentKey = async (keyId) => {
    try {
      await agentApi.updateKey(keyId, { is_active: false });
      loadAgentKeys();
      showToast('success', 'API key revoked');
    } catch { showToast('error', 'Failed to revoke key'); }
  };

  const handleDeleteAgentKey = async (keyId) => {
    if (!window.confirm('Delete this API key? Coordinators using it will lose access.')) return;
    try {
      await agentApi.deleteKey(keyId);
      loadAgentKeys();
      showToast('success', 'API key deleted');
    } catch { showToast('error', 'Failed to delete key'); }
  };

  const handleToggleAgentPolicy = async (keyId, field, currentKeys) => {
    const key = currentKeys.find(k => k.id === keyId);
    if (!key) return;
    const newPolicy = { ...key.sharing_policy, [field]: !key.sharing_policy[field] };
    try {
      await agentApi.updateKey(keyId, { sharing_policy: newPolicy });
      loadAgentKeys();
    } catch { showToast('error', 'Failed to update policy'); }
  };

  // isOESMode and isCoordinatorMode provided by useInstanceMode() hook above

  const fetchOllamaModels = async () => {
    setOllamaModelsLoading(true);
    try {
      const res = await settingsApi.getOllamaModels();
      setOllamaModels((res.data?.models || []).map(m => m.name));
    } catch {
      setOllamaModels([]);
    } finally {
      setOllamaModelsLoading(false);
    }
  };

  const handleSaveAI = async () => {
    setAiSaving(true);
    setError(null);
    try {
      const payload = {
        provider: aiForm.provider || null,
        model: aiForm.model || null,
        host: aiForm.host || null,
        temperature: aiForm.temperature,
        max_tokens: aiForm.max_tokens,
      };
      if (aiForm.api_key) payload.api_key = aiForm.api_key;
      await settingsApi.putAI(payload);
      await loadAIConfig();
      showToast('success', 'AI settings saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save AI settings');
    } finally {
      setAiSaving(false);
    }
  };

  // Sector dependencies presets (must match backend SECTOR_DEPS_PRESETS)
  const SECTOR_DEPS_PRESETS = {
    enisa_nis2: {
      energy: [], telecommunications: ['energy'], water: ['energy'],
      financial: ['energy', 'telecommunications'], banking: ['energy', 'telecommunications'],
      health: ['energy', 'telecommunications', 'water'], transportation: ['energy', 'telecommunications'],
      government: ['energy', 'telecommunications'], manufacturing: ['energy', 'telecommunications', 'water'],
      food_agriculture: ['energy', 'telecommunications', 'water'], chemical: ['energy', 'telecommunications', 'water'],
      defense: ['energy', 'telecommunications', 'government'], information_technology: ['energy', 'telecommunications'],
    },
    nist_ci: {
      energy: [], telecommunications: ['energy'], water: ['energy'],
      financial: ['energy', 'telecommunications'], banking: ['energy', 'telecommunications'],
      health: ['energy', 'telecommunications', 'water'], transportation: ['energy', 'telecommunications'],
      government: ['energy', 'telecommunications'], manufacturing: ['energy', 'telecommunications'],
      food_agriculture: ['energy', 'water'], chemical: ['energy', 'transportation'],
      defense: ['energy', 'telecommunications', 'information_technology'], information_technology: ['energy', 'telecommunications'],
    },
  };

  const ALL_SECTORS = ['energy', 'telecommunications', 'water', 'financial', 'banking', 'health', 'transportation', 'government', 'manufacturing', 'food_agriculture', 'chemical', 'defense', 'information_technology'];
  const SECTOR_DISPLAY = {
    energy: 'Energy', telecommunications: 'Telecom', water: 'Water', financial: 'Financial',
    banking: 'Banking', health: 'Health', transportation: 'Transport', government: 'Government',
    manufacturing: 'Manufacturing', food_agriculture: 'Food & Agri', chemical: 'Chemical',
    defense: 'Defense', information_technology: 'IT',
  };

  const loadSectorDeps = async () => {
    setSectorDepsLoading(true);
    try {
      const res = await settingsApi.getSectorDeps();
      const d = res.data || {};
      setSectorDepsPreset(d.preset || 'enisa_nis2');
      setSectorDepsMatrix(d.matrix || SECTOR_DEPS_PRESETS.enisa_nis2);
    } catch {
      setSectorDepsMatrix(SECTOR_DEPS_PRESETS.enisa_nis2);
    } finally {
      setSectorDepsLoading(false);
    }
  };

  useEffect(() => {
    if (scopesLoaded && scopes.can_edit_global && activeTab === TAB_GENERAL) {
      loadSectorDeps();
    }
  }, [scopesLoaded, scopes.can_edit_global, activeTab]);

  const handleSectorDepsPresetChange = (newPreset) => {
    setSectorDepsPreset(newPreset);
    if (newPreset !== 'custom') {
      setSectorDepsMatrix(SECTOR_DEPS_PRESETS[newPreset] || {});
    }
  };

  const toggleDep = (sector, dependsOn) => {
    if (sectorDepsPreset !== 'custom') return;
    setSectorDepsMatrix(prev => {
      const deps = [...(prev[sector] || [])];
      const idx = deps.indexOf(dependsOn);
      if (idx >= 0) deps.splice(idx, 1);
      else deps.push(dependsOn);
      return { ...prev, [sector]: deps };
    });
  };

  const handleSaveSectorDeps = async () => {
    setSectorDepsSaving(true);
    try {
      await settingsApi.putSectorDeps({ preset: sectorDepsPreset, matrix: sectorDepsPreset === 'custom' ? sectorDepsMatrix : undefined });
      showToast('success', 'Sector dependencies saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save sector dependencies');
    } finally {
      setSectorDepsSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Org/assessment admins must only save to their organization — never send scope 'global'
      const effectiveScope = scopes.can_edit_global ? scope : 'organization';
      const effectiveOrgId = effectiveScope === 'organization' ? organizationId : undefined;
      if (effectiveScope === 'organization' && (effectiveOrgId == null || effectiveOrgId === '')) {
        setError('Please select an organization.');
        setSaving(false);
        return;
      }
      const payload = {
        scope: effectiveScope,
        organization_id: effectiveOrgId,
        cii_criticality_threshold: ciiThreshold,
        scales,
        risk_overall_method: riskMethod,
        risk_residual_mode: riskResidualMode,
      };
      await settingsApi.put(payload);
      await loadSettings();
      showToast('success', 'Settings saved successfully.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateScaleItem = (scaleKey, index, field, value) => {
    setScales((prev) => {
      const next = { ...prev };
      const arr = [...(next[scaleKey] || [])];
      if (!arr[index]) return prev;
      arr[index] = { ...arr[index], [field]: value };
      next[scaleKey] = arr;
      return next;
    });
  };

  const toggleScale = (key) => {
    setExpandedScales((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const effectiveOrgOptions = scopes.organizations || [];
  const canEditGlobal = scopes.can_edit_global;
  const showGlobalOption = canEditGlobal;

  return (
    <Layout>
      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-eucnet-navy">{t('nav.settings')}</h1>
          <p className="mt-2 text-eucnet-grey-600">
            Configure {labels.isCII ? 'CII' : ''} criticality threshold, scales, and system options (e.g. email).
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-eucnet-grey-200 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab(TAB_GENERAL)}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === TAB_GENERAL
                ? 'border-eucnet-sky text-eucnet-navy bg-eucnet-grey-50'
                : 'border-transparent text-eucnet-grey-600 hover:text-eucnet-navy'
            }`}
          >
            General
          </button>
          {scopes.can_edit_global && (
            <button
              type="button"
              onClick={() => setActiveTab(TAB_EMAIL)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === TAB_EMAIL
                  ? 'border-eucnet-sky text-eucnet-navy bg-eucnet-grey-50'
                  : 'border-transparent text-eucnet-grey-600 hover:text-eucnet-navy'
              }`}
            >
              Email
            </button>
          )}
          {scopes.can_edit_global && (
            <button
              type="button"
              onClick={() => setActiveTab(TAB_AI)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === TAB_AI
                  ? 'border-eucnet-sky text-eucnet-navy bg-eucnet-grey-50'
                  : 'border-transparent text-eucnet-grey-600 hover:text-eucnet-navy'
              }`}
            >
              AI
            </button>
          )}
          {scopes.can_edit_global && isOESMode && (
            <button
              type="button"
              onClick={() => setActiveTab(TAB_INTEGRATIONS)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === TAB_INTEGRATIONS
                  ? 'border-eucnet-sky text-eucnet-navy bg-eucnet-grey-50'
                  : 'border-transparent text-eucnet-grey-600 hover:text-eucnet-navy'
              }`}
            >
              Integrations
            </button>
          )}
          {scopes.can_edit_global && isOESMode && (
            <button
              type="button"
              onClick={() => setActiveTab(TAB_AGENT)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === TAB_AGENT
                  ? 'border-eucnet-sky text-eucnet-navy bg-eucnet-grey-50'
                  : 'border-transparent text-eucnet-grey-600 hover:text-eucnet-navy'
              }`}
            >
              Agent
            </button>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {error}
          </div>
        )}

        {/* Scope selector */}
        <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-eucnet-navy mb-4">Scope</h2>
          <div className="flex flex-wrap gap-4 items-center">
            {showGlobalOption && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'global'}
                  onChange={() => { setScope('global'); setOrganizationId(null); }}
                  className="text-eucnet-sky"
                />
                <span>Default (global) settings</span>
              </label>
            )}
            {effectiveOrgOptions.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'organization'}
                  onChange={() => { setScope('organization'); setOrganizationId(effectiveOrgOptions[0]?.id ?? null); }}
                  className="text-eucnet-sky"
                />
                <span>Organization:</span>
                <div className="ml-2 min-w-[12rem]">
                  <CustomSelect
                    value={organizationId ?? ''}
                    onChange={(e) => setOrganizationId(e.target.value ? Number(e.target.value) : null)}
                    options={effectiveOrgOptions.map((org) => ({ value: org.id, label: org.name }))}
                    placeholder="Select organization…"
                    disabled={scope !== 'organization'}
                    searchable={effectiveOrgOptions.length > 8}
                    className="border border-eucnet-grey-300 rounded-lg text-sm"
                  />
                </div>
              </label>
            )}
          </div>
        </div>

        {activeTab === TAB_AGENT && scopes.can_edit_global && isOESMode ? (
          /* Agent tab — OES side API key management */
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-eucnet-navy">Agent API Keys</h2>
                <p className="text-sm text-eucnet-grey-600 mt-1">Generate API keys for coordinators to pull curated assessment data from this instance.</p>
              </div>
              <button
                type="button"
                onClick={() => { setAgentKeyFormOpen(true); setAgentKeyCreated(null); }}
                className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors"
              >
                Generate API Key
              </button>
            </div>

            {/* Show raw key once after creation */}
            {agentKeyCreated && agentKeyCreated.raw_key && (
              <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm font-medium text-emerald-800 mb-2">API key created. Copy it now — it won't be shown again.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-white border border-emerald-300 rounded font-mono text-sm text-slate-800 select-all">{agentKeyCreated.raw_key}</code>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(agentKeyCreated.raw_key); showToast('success', 'Copied to clipboard'); }}
                    className="px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <button type="button" onClick={() => setAgentKeyCreated(null)} className="text-xs text-emerald-600 mt-2 hover:underline">Dismiss</button>
              </div>
            )}

            {agentKeysLoading ? (
              <div className="text-eucnet-grey-500 py-8 text-center">Loading API keys...</div>
            ) : agentKeys.length === 0 && !agentKeyFormOpen ? (
              <div className="text-center py-12 text-slate-400">
                <p className="text-sm">No API keys generated yet.</p>
                <p className="text-xs mt-1">Generate an API key and share it with coordinators who need to pull data from this instance.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agentKeys.map(k => (
                  <div key={k.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">{k.label}</span>
                        <code className="text-[10px] text-slate-400 font-mono">{k.key_prefix}...</code>
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${k.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                          {k.is_active ? 'Active' : 'Revoked'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {k.is_active && (
                          <button onClick={() => handleRevokeAgentKey(k.id)} className="px-2.5 py-1.5 text-[11px] font-medium text-amber-700 border border-amber-200 rounded-md hover:bg-amber-50 transition-colors">
                            Revoke
                          </button>
                        )}
                        <button onClick={() => handleDeleteAgentKey(k.id)} className="px-2.5 py-1.5 text-[11px] font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>
                    {/* Sharing policy toggles */}
                    <div className="flex flex-wrap gap-3 mt-2">
                      {['criticality', 'posture', 'vulnerability_summary', 'risk'].map(field => (
                        <label key={field} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!k.sharing_policy?.[field]}
                            onChange={() => handleToggleAgentPolicy(k.id, field, agentKeys)}
                            disabled={!k.is_active}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-eucnet-sky focus:ring-eucnet-sky disabled:opacity-50"
                          />
                          <span className="text-[11px] text-slate-600 capitalize">{field.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">
                      Created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && <> &middot; Last used {new Date(k.last_used_at).toLocaleString()}</>}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Create key form */}
            {agentKeyFormOpen && (
              <div className="mt-4 border border-eucnet-sky/30 rounded-lg p-5 bg-slate-50/50">
                <h3 className="text-sm font-semibold text-eucnet-navy mb-3">New API Key</h3>
                <div className="space-y-3 max-w-md">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Label</label>
                    <input type="text" value={agentKeyForm.label} onChange={e => setAgentKeyForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. National CSIRT" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Sharing Policy</label>
                    <div className="flex flex-wrap gap-3">
                      {['criticality', 'posture', 'vulnerability_summary', 'risk'].map(field => (
                        <label key={field} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!agentKeyForm.sharing_policy[field]}
                            onChange={() => setAgentKeyForm(p => ({ ...p, sharing_policy: { ...p.sharing_policy, [field]: !p.sharing_policy[field] } }))}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-eucnet-sky focus:ring-eucnet-sky"
                          />
                          <span className="text-[11px] text-slate-600 capitalize">{field.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button type="button" onClick={() => setAgentKeyFormOpen(false)} className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="button" onClick={handleCreateAgentKey} disabled={agentKeySaving || !agentKeyForm.label} className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors disabled:opacity-50">
                    {agentKeySaving ? 'Generating...' : 'Generate Key'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === TAB_INTEGRATIONS && scopes.can_edit_global ? (
          /* Integrations tab */
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-eucnet-navy">Integrations</h2>
                <p className="text-sm text-eucnet-grey-600 mt-1">Connect external platforms to enrich assessments with third-party telemetry.</p>
              </div>
              <button
                type="button"
                onClick={() => { resetIntForm(); setIntFormOpen(true); }}
                className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors"
              >
                Add Integration
              </button>
            </div>

            {intLoading ? (
              <div className="text-eucnet-grey-500 py-8 text-center">Loading integrations...</div>
            ) : integrations.length === 0 && !intFormOpen ? (
              <div className="text-center py-12 text-slate-400">
                <p className="text-sm">No integrations configured yet.</p>
                <p className="text-xs mt-1">Click "Add Integration" to connect external security platforms.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {integrations.map(intg => (
                  <div key={intg.id} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">{intg.name}</span>
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 rounded">
                          {intg.integration_type === 'crowdstrike_falcon' ? 'CrowdStrike Falcon' : intg.integration_type}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${intg.is_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {intg.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {(intg.oes_mappings || []).length} OES mapped
                        {intg.oes_mappings?.length > 0 && ` (${intg.oes_mappings.map(m => m.oes_name || `OES #${m.oes_id}`).join(', ')})`}
                      </p>
                      {intTestResult && intTestResult.id === intg.id && (
                        <p className={`text-xs mt-1 ${intTestResult.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                          {intTestResult.ok ? 'Connection successful' : `Connection failed: ${intTestResult.error}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => handleIntTest(intg.id)} disabled={intTesting === intg.id} className="px-2.5 py-1.5 text-[11px] font-medium border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors">
                        {intTesting === intg.id ? 'Testing...' : 'Test'}
                      </button>
                      <button onClick={() => handleIntToggle(intg)} className="px-2.5 py-1.5 text-[11px] font-medium border border-slate-300 rounded-md hover:bg-slate-50 transition-colors">
                        {intg.is_enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => openIntEdit(intg)} className="px-2.5 py-1.5 text-[11px] font-medium border border-slate-300 rounded-md hover:bg-slate-50 transition-colors">
                        Edit
                      </button>
                      <button onClick={() => handleIntDelete(intg.id)} className="px-2.5 py-1.5 text-[11px] font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit Form */}
            {intFormOpen && (
              <div className="mt-4 border border-eucnet-sky/30 rounded-lg p-5 bg-slate-50/50">
                <h3 className="text-sm font-semibold text-eucnet-navy mb-3">{intEditing ? 'Edit Integration' : 'New Integration'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                    <input type="text" value={intForm.name} onChange={e => setIntForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Falcon US-1" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                    <select value={intForm.integration_type} onChange={e => setIntForm(p => ({ ...p, integration_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent bg-white">
                      <option value="crowdstrike_falcon">CrowdStrike Falcon</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Client ID</label>
                    <input type="text" value={intForm.client_id} onChange={e => setIntForm(p => ({ ...p, client_id: e.target.value }))} placeholder="Falcon API Client ID" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Client Secret</label>
                    <input type="password" value={intForm.client_secret} onChange={e => setIntForm(p => ({ ...p, client_secret: e.target.value }))} placeholder={intEditing ? 'Leave blank to keep current' : 'Falcon API Client Secret'} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Region</label>
                    <select value={intForm.region} onChange={e => {
                      const regionUrls = { 'us-1': 'https://api.crowdstrike.com', 'us-2': 'https://api.us-2.crowdstrike.com', 'eu-1': 'https://api.eu-1.crowdstrike.com' };
                      setIntForm(p => ({ ...p, region: e.target.value, base_url: regionUrls[e.target.value] || p.base_url }));
                    }} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent bg-white">
                      <option value="us-1">US-1 (api.crowdstrike.com)</option>
                      <option value="us-2">US-2 (api.us-2.crowdstrike.com)</option>
                      <option value="eu-1">EU-1 (api.eu-1.crowdstrike.com)</option>
                    </select>
                  </div>
                  {!pureOES && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Map to OES</label>
                    <div className="max-h-32 overflow-y-auto border border-slate-300 rounded-lg p-2 bg-white">
                      {oesList.length === 0 ? (
                        <p className="text-xs text-slate-400">No OES found</p>
                      ) : oesList.map(oes => (
                        <label key={oes.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={intForm.oes_ids.includes(oes.id)}
                            onChange={e => {
                              setIntForm(p => ({
                                ...p,
                                oes_ids: e.target.checked ? [...p.oes_ids, oes.id] : p.oes_ids.filter(id => id !== oes.id),
                              }));
                            }}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-eucnet-sky focus:ring-eucnet-sky"
                          />
                          <span className="text-xs text-slate-700">{oes.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button type="button" onClick={() => { setIntFormOpen(false); resetIntForm(); }} className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="button" onClick={handleIntSave} disabled={intSaving || !intForm.name || !intForm.client_id} className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors disabled:opacity-50">
                    {intSaving ? 'Saving...' : intEditing ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === TAB_AI && scopes.can_edit_global ? (
          /* AI tab */
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6">
            <h2 className="text-lg font-semibold text-eucnet-navy mb-2">AI Configuration</h2>
            <p className="text-sm text-eucnet-grey-600 mb-4">
              Configure the LLM provider and model used for "Assess with AI" suggestions in criticality assessments.
            </p>
            {aiLoading ? (
              <div className="text-eucnet-grey-500 py-4">Loading AI settings...</div>
            ) : (
              <div className="space-y-4 max-w-xl">
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Provider</label>
                  <CustomSelect
                    value={aiForm.provider}
                    onChange={(e) => {
                      const prov = e.target.value;
                      setAiForm((f) => ({
                        ...f,
                        provider: prov,
                        model: DEFAULT_MODELS[prov]?.[0] || '',
                        host: prov === 'ollama' ? f.host : '',
                      }));
                    }}
                    options={AI_PROVIDERS}
                    placeholder="Select provider..."
                    className="border border-eucnet-grey-300 rounded-lg text-sm"
                  />
                </div>
                {aiForm.provider === 'ollama' && (
                  <div>
                    <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Ollama Host URL</label>
                    <input
                      type="text"
                      value={aiForm.host}
                      onChange={(e) => setAiForm((f) => ({ ...f, host: e.target.value }))}
                      className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="e.g. http://localhost:11434"
                    />
                  </div>
                )}
                {(aiForm.provider === 'openai' || aiForm.provider === 'anthropic') && (
                  <div>
                    <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">API Key</label>
                    <input
                      type="password"
                      value={aiForm.api_key}
                      onChange={(e) => setAiForm((f) => ({ ...f, api_key: e.target.value }))}
                      className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Leave blank to keep current"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Model</label>
                  {aiForm.provider === 'ollama' ? (
                    <>
                      <div className="flex gap-2">
                        <CustomSelect
                          value={aiForm.model}
                          onChange={(e) => setAiForm((f) => ({ ...f, model: e.target.value }))}
                          options={
                            ollamaModels.length > 0
                              ? ollamaModels.map(m => ({ value: m, label: m }))
                              : DEFAULT_MODELS.ollama.map(m => ({ value: m, label: m }))
                          }
                          placeholder={ollamaModelsLoading ? 'Loading models...' : 'Select model...'}
                          className="flex-1 border border-eucnet-grey-300 rounded-lg text-sm"
                        />
                        <button
                          type="button"
                          onClick={fetchOllamaModels}
                          disabled={ollamaModelsLoading || !aiForm.host}
                          className="px-3 py-2 text-xs font-medium border border-eucnet-grey-300 rounded-lg text-eucnet-grey-600 hover:bg-eucnet-grey-50 disabled:opacity-50"
                          title="Fetch available models from Ollama"
                        >
                          {ollamaModelsLoading ? '...' : 'Refresh'}
                        </button>
                      </div>
                      <p className="text-xs text-eucnet-grey-500 mt-1">
                        {ollamaModels.length > 0
                          ? `${ollamaModels.length} model(s) available on server`
                          : 'Enter the host URL above, then click Refresh to load installed models.'
                        }
                      </p>
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <CustomSelect
                        value={aiForm.model}
                        onChange={(e) => setAiForm((f) => ({ ...f, model: e.target.value }))}
                        options={(DEFAULT_MODELS[aiForm.provider] || []).map(m => ({ value: m, label: m }))}
                        placeholder="Select model..."
                        className="flex-1 border border-eucnet-grey-300 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={aiForm.model}
                        onChange={(e) => setAiForm((f) => ({ ...f, model: e.target.value }))}
                        className="flex-1 border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="Or type custom model"
                      />
                    </div>
                  )}
                </div>
                {/* Advanced settings (collapsed by default) */}
                <div className="border border-eucnet-grey-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setAiAdvancedOpen(!aiAdvancedOpen)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-eucnet-grey-50 hover:bg-eucnet-grey-100 text-left text-sm font-medium text-eucnet-grey-600"
                  >
                    <span>Advanced</span>
                    <span className="text-eucnet-grey-400 text-xs">{aiAdvancedOpen ? '▼' : '▶'}</span>
                  </button>
                  {aiAdvancedOpen && (
                    <div className="p-4 border-t border-eucnet-grey-200 grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Temperature</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="2"
                          value={aiForm.temperature}
                          onChange={(e) => setAiForm((f) => ({ ...f, temperature: parseFloat(e.target.value) || 0.2 }))}
                          className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                        />
                        <p className="text-xs text-eucnet-grey-500 mt-1">Lower = more deterministic (0.1–0.3 recommended)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Max Tokens</label>
                        <input
                          type="number"
                          min="500"
                          max="16000"
                          value={aiForm.max_tokens}
                          onChange={(e) => setAiForm((f) => ({ ...f, max_tokens: parseInt(e.target.value, 10) || 4000 }))}
                          className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleSaveAI}
                    disabled={aiSaving}
                    className="px-6 py-2.5 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50"
                  >
                    {aiSaving ? 'Saving...' : 'Save AI settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === TAB_EMAIL && scopes.can_edit_global ? (
          /* Email tab */
          <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6">
            <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Email (SMTP) configuration</h2>
            <p className="text-sm text-eucnet-grey-600 mb-4">
              Configure outgoing email for password resets and invitations. Leave password blank to keep the current value.
            </p>
            {systemLoading ? (
              <div className="text-eucnet-grey-500 py-4">Loading email settings…</div>
            ) : (
              <div className="space-y-4 max-w-xl">
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">SMTP server</label>
                  <input
                    type="text"
                    value={emailForm.smtp_server}
                    onChange={(e) => setEmailForm((f) => ({ ...f, smtp_server: e.target.value }))}
                    className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. smtp.gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">SMTP port</label>
                  <input
                    type="number"
                    value={emailForm.smtp_port}
                    onChange={(e) => setEmailForm((f) => ({ ...f, smtp_port: parseInt(e.target.value, 10) || 587 }))}
                    className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Username</label>
                  <input
                    type="text"
                    value={emailForm.smtp_username}
                    onChange={(e) => setEmailForm((f) => ({ ...f, smtp_username: e.target.value }))}
                    className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={emailForm.smtp_password}
                    onChange={(e) => setEmailForm((f) => ({ ...f, smtp_password: e.target.value }))}
                    className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Leave blank to keep current"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-eucnet-grey-700 mb-1">From (sender) address</label>
                  <input
                    type="text"
                    value={emailForm.smtp_sender}
                    onChange={(e) => setEmailForm((f) => ({ ...f, smtp_sender: e.target.value }))}
                    className="w-full border border-eucnet-grey-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. noreply@example.com"
                  />
                </div>
                <div className="flex flex-wrap gap-6 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailForm.smtp_use_tls}
                      onChange={(e) => setEmailForm((f) => ({ ...f, smtp_use_tls: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">Use TLS</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailForm.smtp_use_ssl}
                      onChange={(e) => setEmailForm((f) => ({ ...f, smtp_use_ssl: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">Use SSL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailForm.smtp_starttls}
                      onChange={(e) => setEmailForm((f) => ({ ...f, smtp_starttls: e.target.checked }))}
                      className="rounded text-eucnet-sky"
                    />
                    <span className="text-sm">STARTTLS</span>
                  </label>
                </div>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleSaveEmail}
                    disabled={systemSaving}
                    className="px-6 py-2.5 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50"
                  >
                    {systemSaving ? 'Saving…' : 'Save email settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="text-eucnet-grey-500 py-8">Loading settings…</div>
        ) : (
          <>
            {/* Criticality threshold — labels from current criticality scale */}
            <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-eucnet-navy mb-2">{labels.isCII ? 'CII criticality' : 'Criticality'} threshold</h2>
              <p className="text-sm text-eucnet-grey-600 mb-4">
                Minimum criticality level for {labels.isCII ? `an ${labels.entityName} to be considered CII` : `an ${labels.entityName} to be eligible`} (and eligible for posture and risk assessment).
              </p>
              <div className="flex flex-wrap gap-3">
                {(Array.isArray(scales?.criticality) && scales.criticality.length > 0 ? scales.criticality : DEFAULT_CRITICALITY_OPTIONS).map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="cii_threshold"
                      checked={ciiThreshold === opt.value}
                      onChange={() => setCiiThreshold(opt.value)}
                      className="text-eucnet-sky"
                    />
                    <span className="text-sm">{opt.label ?? opt.value}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Risk matrix overall aggregation */}
            <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Risk matrix overall aggregation</h2>
              <p className="text-sm text-eucnet-grey-600 mb-4">
                Choose how the overall risk indicator is calculated for the asset-centric risk matrix.
              </p>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="risk_overall_method"
                    className="mt-1 text-eucnet-sky"
                    checked={riskMethod === 'max'}
                    onChange={() => setRiskMethod('max')}
                  />
                  <div>
                    <div className="text-sm font-medium text-eucnet-navy">Maximum (worst-case)</div>
                    <div className="text-xs text-eucnet-grey-600">
                      Overall risk is the highest risk across all asset–threat combinations (current default).
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="risk_overall_method"
                    className="mt-1 text-eucnet-sky"
                    checked={riskMethod === 'average'}
                    onChange={() => setRiskMethod('average')}
                  />
                  <div>
                    <div className="text-sm font-medium text-eucnet-navy">Average</div>
                    <div className="text-xs text-eucnet-grey-600">
                      Overall risk is the average of all asset–threat risks (less sensitive to a single outlier).
                    </div>
                  </div>
                </label>
              </div>

              {/* Residual-risk mode */}
              <div className="mt-5 pt-5 border-t border-eucnet-grey-200">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 text-eucnet-sky"
                    checked={riskResidualMode}
                    onChange={(e) => setRiskResidualMode(e.target.checked)}
                  />
                  <div>
                    <div className="text-sm font-medium text-eucnet-navy">Compliance-weighted residual risk</div>
                    <div className="text-xs text-eucnet-grey-600">
                      Include compliant controls as low residual risk when computing the overall indicator.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Assessment scales */}
            <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Assessment scales</h2>
              <p className="text-sm text-eucnet-grey-600 mb-4">
                Edit labels and descriptions for criticality and other assessment scales.
              </p>
              {scales && Object.keys(scales).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(scales).map(([key, items]) => {
                    const isExpanded = expandedScales[key] !== false;
                    const label = SCALE_LABELS[key] || key;
                    const arr = Array.isArray(items) ? items : [];
                    return (
                      <div key={key} className="border border-eucnet-grey-200 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleScale(key)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-eucnet-grey-50 hover:bg-eucnet-grey-100 text-left font-medium text-eucnet-navy"
                        >
                          <span>{label}</span>
                          <span className="text-eucnet-grey-500">{isExpanded ? '▼' : '▶'}</span>
                        </button>
                        {isExpanded && (
                          <div className="p-4 bg-white border-t border-eucnet-grey-200">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-eucnet-grey-600 border-b">
                                  <th className="pb-2 pr-4">Value</th>
                                  <th className="pb-2 pr-4">Label</th>
                                  <th className="pb-2">Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {arr.map((item, idx) => (
                                  <tr key={idx} className="border-b border-eucnet-grey-100">
                                    <td className="py-2 pr-4 text-eucnet-grey-600">{item.value}</td>
                                    <td className="py-2 pr-4">
                                      <input
                                        type="text"
                                        value={item.label ?? ''}
                                        onChange={(e) => updateScaleItem(key, idx, 'label', e.target.value)}
                                        className="w-full border border-eucnet-grey-300 rounded px-2 py-1"
                                      />
                                    </td>
                                    <td className="py-2">
                                      {item.desc !== undefined ? (
                                        <input
                                          type="text"
                                          value={item.desc ?? ''}
                                          onChange={(e) => updateScaleItem(key, idx, 'desc', e.target.value)}
                                          className="w-full border border-eucnet-grey-300 rounded px-2 py-1"
                                          placeholder="Optional"
                                        />
                                      ) : (
                                        <span className="text-eucnet-grey-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-eucnet-grey-500">No scale data loaded.</p>
              )}
            </div>

            {/* Sector Dependencies */}
            {labels.showSectorDeps && scopes.can_edit_global && (
              <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Sector Dependencies</h2>
                <p className="text-sm text-eucnet-grey-600 mb-4">
                  Define which sectors depend on which for the dependency network visualization and interdependency analysis.
                </p>
                {sectorDepsLoading ? (
                  <div className="text-eucnet-grey-500 py-4">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-eucnet-grey-700">Model:</label>
                      <CustomSelect
                        value={sectorDepsPreset}
                        onChange={(e) => handleSectorDepsPresetChange(e.target.value)}
                        options={[
                          { value: 'enisa_nis2', label: 'ENISA NIS2 (EU)' },
                          { value: 'nist_ci', label: 'NIST Critical Infrastructure (US)' },
                          { value: 'custom', label: 'Custom' },
                        ]}
                        placeholder="Select preset..."
                        searchable={false}
                        className="w-64 border border-eucnet-grey-300 rounded-lg text-sm"
                      />
                      {sectorDepsPreset !== 'custom' && (
                        <span className="text-xs text-eucnet-grey-500 italic">Read-only preset. Select "Custom" to edit.</span>
                      )}
                    </div>
                    {/* Interactive grid */}
                    <div className="overflow-x-auto">
                      <table className="text-[10px] border-collapse">
                        <thead>
                          <tr>
                            <th className="p-1.5 text-left text-eucnet-grey-500 font-semibold border-b border-eucnet-grey-200">
                              <span className="text-[9px]">Row depends on Col →</span>
                            </th>
                            {ALL_SECTORS.map(col => (
                              <th key={col} className="p-1.5 text-center font-semibold text-eucnet-grey-600 border-b border-eucnet-grey-200" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 70 }}>
                                {SECTOR_DISPLAY[col]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ALL_SECTORS.map(row => (
                            <tr key={row} className="hover:bg-eucnet-grey-50">
                              <td className="p-1.5 font-semibold text-eucnet-grey-700 border-b border-eucnet-grey-100 whitespace-nowrap">
                                {SECTOR_DISPLAY[row]}
                              </td>
                              {ALL_SECTORS.map(col => {
                                const isSelf = row === col;
                                const isChecked = (sectorDepsMatrix[row] || []).includes(col);
                                const isEditable = sectorDepsPreset === 'custom' && !isSelf;
                                return (
                                  <td key={col} className="p-1 text-center border-b border-eucnet-grey-100">
                                    {isSelf ? (
                                      <span className="text-eucnet-grey-300">—</span>
                                    ) : (
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleDep(row, col)}
                                        disabled={!isEditable}
                                        className={`w-3.5 h-3.5 rounded ${isEditable ? 'text-eucnet-blue cursor-pointer' : 'text-eucnet-grey-400 cursor-not-allowed'}`}
                                      />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={handleSaveSectorDeps}
                        disabled={sectorDepsSaving}
                        className="px-4 py-2 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50 text-sm"
                      >
                        {sectorDepsSaving ? 'Saving...' : 'Save dependencies'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sector Lists Management */}
            <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-eucnet-navy mb-2">Sector Lists</h2>
              <p className="text-sm text-eucnet-grey-600 mb-4">
                Create and manage custom sector lists for your organization. Predefined standards like NIS2 are available globally.
              </p>
              <SectorListsManager
                organizationId={scope === 'organization' ? organizationId : null}
                canEditGlobal={scopes.can_edit_global}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-eucnet-sky text-white font-medium rounded-lg hover:bg-eucnet-sky/90 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
